import { useAppDispatch } from "../../../app/features/store/store";
import * as Yup from "yup";
import { toast } from "react-toastify";
import getAxiosError from "../../../app/api/axiosError";
import { Form, Formik } from "formik";
import SubmitModalWithFormik from "../../../components/common/modalPopup/SubmitModalWithFormik";
import { Box, Grid, MenuItem } from "@mui/material";
import FormikTextField from "../../../components/common/formikFields/FormikTextField";
import { updateUserTokenToInvalid } from "../../../app/features/login/loginSlice";
import { AddFdpRequest } from "../../../app/models/requestModels/fdpRequest";
import { addNewfdp } from "../../../app/features/fdp/fdpSlice";
import { STATES_LIST, EMAIL_REGEX } from "../FdpConstants";

interface AddFdpModalProps {
  closeAddFdpModal: CallableFunction;
  onCompleted: CallableFunction;
}
function AddFdpFormModel(
  companyName: string,
  ownerFirstName: string,
  ownerLastName: string,
  email: string,
  dispatcherName: string,
  primaryPhone: string,
  secondaryPhone: string,
  primaryMsa: string,
  secondaryMsa: string,
  address: string,
  state: string,
  city: string,
  zipCode: string
): AddFdpRequest {
  return {
    companyName: companyName,
    ownerFirstName: ownerFirstName,
    ownerLastName: ownerLastName,
    email: email,
    primaryPhone: primaryPhone,
    secondaryPhone: secondaryPhone,
    dispatcherName: dispatcherName,
    primaryMsa: primaryMsa,
    secondaryMsa: secondaryMsa,
    address: address,
    city: city,
    state: state,
    zipCode: zipCode,
  };
}

const AddFdpModal = ({ closeAddFdpModal, onCompleted }: AddFdpModalProps) => {
  const dispatch = useAppDispatch();
  const initialValues: AddFdpRequest = {
    companyName: "",
    ownerFirstName: "",
    ownerLastName: "",
    email: "",
    primaryPhone: "",
    secondaryPhone: "",
    dispatcherName: "",
    primaryMsa: "",
    secondaryMsa: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
  };

  const validationSchema = Yup.object().shape({
    companyName: Yup.string().required("Company Name is required"),
    ownerFirstName: Yup.string().required("Owner name is Required"),
    ownerLastName: Yup.string().matches(
      /^[^\s].*[^\s]$/,
      "Last name can't contain extra blank spaces"
    ),
    email: Yup.string()
      .required("Email is Required")
      .matches(EMAIL_REGEX, "Invalid Email Format"),
    primaryPhone: Yup.string()
      .required("Phone is required")
      .matches(
        /^\(\d{3}\) \d{3}-\d{4}$/,
        "Provide valid phone number, ex (XXX) XXX-XXXX"
      ),
    secondaryPhone: Yup.string().matches(
      /^\(\d{3}\) \d{3}-\d{4}$/,
      "Provide valid phone number, ex (XXX) XXX-XXXX"
    ),
    address: Yup.string().required("Address is Required"),
    primaryMsa: Yup.string().required("Primary MSA is Required"),
    city: Yup.string().required("City is Required"),
    state: Yup.string().required("State is Required"),
    zipCode: Yup.string()
      .required("Zipcode is Required")
      .matches(
        /^\d{5}(?:-\d{4})?$/,
        "Provide valid zipcode, ex XXXXX or XXXXX-XXXX"
      ),
  });

  // ***** Handlers *****
  const handleSubmit = async (values: AddFdpRequest) => {
    try {
      const addUserDetails = AddFdpFormModel(
        values.companyName.trim(),
        values.ownerFirstName.trim(),
        values.ownerLastName.trim(),
        values.email,
        values.dispatcherName.trim(),
        values.primaryPhone,
        values.secondaryPhone,
        values.primaryMsa.trim(),
        values.secondaryMsa.trim(),
        values.address.trim(),
        values.state.trim(),
        values.city.trim(),
        values.zipCode
      );
      const response = await dispatch(addNewfdp(addUserDetails)).unwrap();
      if (response.isSuccess) {
        toast.success(response.message!, {
          toastId: "addFdpSuccess",
        });
        onCompleted();
      } else {
        toast.error(response.message!, {
          toastId: "addFdpError",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "addFdpException",
      });
    }
    return true;
  };
  return (
    <Formik
      initialValues={{ ...initialValues }}
      validationSchema={validationSchema}
      onSubmit={(values: AddFdpRequest, { setSubmitting }) => {
        setSubmitting(true);
        handleSubmit(values);
        setSubmitting(false);
      }}
    >
      <SubmitModalWithFormik
        title="Add New FDP Details"
        closeSubmitModal={closeAddFdpModal}
        childErrorMessage={""}
        allowSubmit={false}
        size="lg"
        isLoading={false}
        submitButtonName="Save"
        closeModalAfterSubmit={true}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Form>
            <Grid container rowSpacing={2} columnSpacing={2}>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormikTextField
                  margin="normal"
                  id="companyName"
                  label="Company Name"
                  name="companyName"
                  size="small"
                  fullWidth
                  variant={"outlined"}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormikTextField
                  margin="normal"
                  id="dispatcherName"
                  label="Dispatcher Name"
                  name="dispatcherName"
                  size="small"
                  fullWidth
                  variant={"outlined"}
                />
              </Grid>
            </Grid>
            <Grid container rowSpacing={2} columnSpacing={2}>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormikTextField
                  margin="normal"
                  id="ownerFirstName"
                  label="Owner Firstname"
                  name="ownerFirstName"
                  size="small"
                  fullWidth
                  variant={"outlined"}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormikTextField
                  margin="normal"
                  id="ownerLastName"
                  label="Owner Lastname"
                  name="ownerLastName"
                  size="small"
                  fullWidth
                  variant={"outlined"}
                />
              </Grid>
            </Grid>
            <Grid container rowSpacing={2} columnSpacing={2}>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormikTextField
                  margin="normal"
                  id="primaryPhone"
                  label="Primary Phone"
                  name="primaryPhone"
                  size="small"
                  fullWidth
                  variant={"outlined"}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormikTextField
                  margin="normal"
                  id="secondaryPhone"
                  label="Secondary Phone"
                  name="secondaryPhone"
                  size="small"
                  fullWidth
                  variant={"outlined"}
                />
              </Grid>
            </Grid>
            <Grid container rowSpacing={2} columnSpacing={2}>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormikTextField
                  margin="normal"
                  id="primaryMsa"
                  label="Primary MSA"
                  name="primaryMsa"
                  size="small"
                  fullWidth
                  variant={"outlined"}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormikTextField
                  margin="normal"
                  id="secondaryMsa"
                  label="Secondary MSA"
                  name="secondaryMsa"
                  size="small"
                  fullWidth
                  variant={"outlined"}
                />
              </Grid>
            </Grid>
            <Grid container rowSpacing={2} columnSpacing={2}>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormikTextField
                  margin="normal"
                  id="address"
                  label="Address"
                  name="address"
                  size="small"
                  fullWidth
                  variant={"outlined"}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormikTextField
                  margin="normal"
                  id="city"
                  label="City"
                  name="city"
                  size="small"
                  fullWidth
                  variant={"outlined"}
                  required
                />
              </Grid>
            </Grid>
            <Grid container rowSpacing={2} columnSpacing={2}>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormikTextField
                  select
                  margin="normal"
                  id="state"
                  label="State"
                  name="state"
                  size="small"
                  fullWidth
                  variant={"outlined"}
                  SelectProps={{
                    MenuProps: { PaperProps: { style: { maxHeight: 150 } } },
                  }}
                  required
                >
                  {STATES_LIST.map((state, index) => (
                    <MenuItem key={index} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </FormikTextField>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormikTextField
                  margin="normal"
                  id="zipCode"
                  label="Zipcode"
                  name="zipCode"
                  size="small"
                  fullWidth
                  variant="outlined"
                  required
                />
              </Grid>
            </Grid>
            <Grid container rowSpacing={2} columnSpacing={2}>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormikTextField
                  margin="normal"
                  id="email"
                  label="Email"
                  name="email"
                  size="small"
                  fullWidth
                  variant={"outlined"}
                  required
                />
              </Grid>
            </Grid>
          </Form>
        </Box>
      </SubmitModalWithFormik>
    </Formik>
  );
};

export default AddFdpModal;
